﻿import { analyticsPushFunc, openWhatsAppShare } from "../../../../widgetsUtils"
import { ShareMenu } from "../../../../siteWidgets/socialShareButtons/ShareMenu/shareMenu"
import { ArticleHeader1280AiSummary, SummaryDataItem } from "../articleHeaderComponenta/articleHeader1280AiSummary"
import {
    WCM_COMMENTS_EVENT,
    WCM_READ_ALOUD_REQUEST_EVENT,
    WCM_READ_ALOUD_STATE_EVENT,
    dispatchBepoRecommendationsOpen,
    formatCommentCountMagazine,
    formatReadAloudDuration,
    resolveReadAloudData,
    resolveReadAloudMediaImageUrl,
    ReadAloudRequestDetail,
    ReadAloudStateDetail,
} from "../articleHeaderComponenta/actionBarShared"
import { BepoFloatingPlayerController } from "../../../radioWidgets/bepoFloatingPlayer/bepoFloatingPlayerController"
import { YplusPremiumGated } from "../../../../../global/paywall/yplusPremiumGated"

declare var window: Window & {
    wcmAudioPlayer: { stop: () => void; togglePlayPause?: () => void }
}

let activeReadAloudPlayerHost: SiteArticleActionBar1280 | null = null

interface ActionBarProps {
    categorySubChannel: string
    author: string
    articleId: string
    showComments: boolean
    showShare?: boolean
    lang?: string
    title?: string
    verticalSidebar?: boolean
    shouldRenderSummary?: boolean
    summaryData?: SummaryDataItem[]
    desktopSummaryMountId?: string
    isMagazine?: boolean
    readAloudUrl?: string | null
    readAloudDuration?: number | null
    mediaImageUrl?: string
    domain?: string
    imagesDomain?: string
    isReadAloudPlayerHost?: boolean
}

interface ActionBarState {
    wcmNumOfComments: string
    isReadAloudOpen: boolean
    isReadAloudPlaying: boolean
}

export class SiteArticleActionBar1280 extends React.Component<ActionBarProps, ActionBarState> {
    static siteScriptName = "SiteArticleActionBar1280"

    constructor() {
        super();
        this.state = {
            wcmNumOfComments: "",
            isReadAloudOpen: false,
            isReadAloudPlaying: false,
        }
    }

    componentDidMount() {
        document.addEventListener(WCM_COMMENTS_EVENT, this.handleOnWcmCommentsRecived);
        document.addEventListener(WCM_READ_ALOUD_STATE_EVENT, this.handleReadAloudState);
        // Only claim host when this bar has playable read-aloud data. Otherwise a
        // sticky/secondary bar (e.g. magazine header) can steal the host slot and
        // open requests from the main bar silently no-op.
        if (this.isPlayerHostCandidate() && !activeReadAloudPlayerHost && this.getResolvedReadAloud()) {
            activeReadAloudPlayerHost = this
            document.addEventListener(WCM_READ_ALOUD_REQUEST_EVENT, this.handleReadAloudRequest);
        }
    }

    componentWillUnmount() {
        document.removeEventListener(WCM_COMMENTS_EVENT, this.handleOnWcmCommentsRecived);
        document.removeEventListener(WCM_READ_ALOUD_STATE_EVENT, this.handleReadAloudState);
        if (activeReadAloudPlayerHost === this) {
            document.removeEventListener(WCM_READ_ALOUD_REQUEST_EVENT, this.handleReadAloudRequest);
            activeReadAloudPlayerHost = null
        }
    }

    private isPlayerHostCandidate = () => {
        const { isReadAloudPlayerHost, verticalSidebar } = this.props
        if (typeof isReadAloudPlayerHost === "boolean") {
            return isReadAloudPlayerHost
        }
        return !verticalSidebar
    }

    private isActivePlayerHost = () => {
        return activeReadAloudPlayerHost === this
    }

    private handleOnWcmCommentsRecived = (e) => {
        this.setState({ wcmNumOfComments: e.detail.wcmNumOfComments });
    }

    private handleReadAloudState = (e: CustomEvent) => {
        const detail = e.detail as ReadAloudStateDetail
        if (!detail) {
            return
        }
        this.setState({
            isReadAloudOpen: detail.isOpen,
            isReadAloudPlaying: detail.isPlaying,
        })
    }

    private handleReadAloudRequest = (e: CustomEvent) => {
        const detail = e.detail as ReadAloudRequestDetail
        if (!detail || !this.isActivePlayerHost()) {
            return
        }
        if (detail.action === "open") {
            this.openReadAloudPlayer()
        } else if (detail.action === "toggle") {
            this.toggleReadAloudPlayback()
        }
    }

    private dispatchReadAloudState = (isOpen: boolean, isPlaying: boolean) => {
        if (typeof document === "undefined") {
            return
        }
        document.dispatchEvent(new CustomEvent(WCM_READ_ALOUD_STATE_EVENT, {
            detail: { isOpen, isPlaying } as ReadAloudStateDetail,
        }))
    }

    private getResolvedReadAloud = () => {
        const { readAloudUrl, readAloudDuration } = this.props
        return resolveReadAloudData(readAloudUrl, readAloudDuration)
    }

    private openReadAloudPlayer = () => {
        if (!this.getResolvedReadAloud()) {
            return
        }
        this.setState({ isReadAloudOpen: true, isReadAloudPlaying: true })
        this.dispatchReadAloudState(true, true)
    }

    private toggleReadAloudPlayback = () => {
        if (window.wcmAudioPlayer && typeof window.wcmAudioPlayer.togglePlayPause === "function") {
            window.wcmAudioPlayer.togglePlayPause()
            return
        }
        this.openReadAloudPlayer()
    }

    private claimReadAloudPlayerHost = () => {
        if (activeReadAloudPlayerHost && activeReadAloudPlayerHost !== this) {
            return false
        }
        activeReadAloudPlayerHost = this
        document.addEventListener(WCM_READ_ALOUD_REQUEST_EVENT, this.handleReadAloudRequest)
        return true
    }

    private handleReadAloudBtnClicked = () => {
        const { isReadAloudOpen } = this.state
        if (!this.getResolvedReadAloud()) {
            return
        }

        dispatchBepoRecommendationsOpen()

        if (!this.isActivePlayerHost()) {
            if (!activeReadAloudPlayerHost) {
                this.claimReadAloudPlayerHost()
                this.openReadAloudPlayer()
                return
            }
            document.dispatchEvent(new CustomEvent(WCM_READ_ALOUD_REQUEST_EVENT, {
                detail: { action: isReadAloudOpen ? "toggle" : "open" } as ReadAloudRequestDetail,
            }))
            return
        }

        if (!isReadAloudOpen) {
            this.openReadAloudPlayer()
            return
        }
        this.toggleReadAloudPlayback()
    }

    private handleReadAloudClose = () => {
        this.setState({ isReadAloudOpen: false, isReadAloudPlaying: false })
        this.dispatchReadAloudState(false, false)
    }

    private handleReadAloudPlayingChange = (isPlaying: boolean) => {
        this.setState({ isReadAloudPlaying: isPlaying })
        this.dispatchReadAloudState(true, isPlaying)
    }

    private handleCommentsBtnClicked = () => {
        const elementToScroll = document.querySelector("#SiteArticleComments")
        const commentsPopUp = document.querySelector(".ArticleCommentsPopup")
        if (elementToScroll && !commentsPopUp) {
            setTimeout(() => {
                elementToScroll.scrollIntoView({ behavior: "smooth", block: "center" })
            }, 0)
        }
        if (commentsPopUp) {
            typeof window !== "undefined" && window.handleCommentsPopupVisability && window.handleCommentsPopupVisability()
        }
    }

    private renderReadAloudPlayer = () => {
        const { title, mediaImageUrl, domain, imagesDomain } = this.props
        const { isReadAloudOpen } = this.state
        const readAloud = this.getResolvedReadAloud()
        if (!this.isActivePlayerHost() || !isReadAloudOpen || !readAloud) {
            return null
        }

        const resolvedMediaImageUrl = resolveReadAloudMediaImageUrl(mediaImageUrl, imagesDomain)

        return (
            <BepoFloatingPlayerController
                title={title || ""}
                podcastName=""
                podcastImageUrl=""
                podcastPageUrl=""
                bepoHomeUrl={domain ? `${domain}/tags/bepo` : "/tags/bepo"}
                audioUrl={readAloud.url}
                mediaImageUrl={resolvedMediaImageUrl}
                duration={readAloud.durationSeconds || 0}
                footerVariant="sponsor"
                onClose={this.handleReadAloudClose}
                onPlayingChange={this.handleReadAloudPlayingChange}
            />
        )
    }

    render() {
        const {
            showComments,
            showShare,
            articleId,
            lang,
            title,
            verticalSidebar,
            shouldRenderSummary,
            summaryData,
            desktopSummaryMountId,
            isMagazine,
        } = this.props
        const { wcmNumOfComments, isReadAloudOpen, isReadAloudPlaying } = this.state
        const hasNoComments = !wcmNumOfComments || wcmNumOfComments === "0"
        const commentNumber = hasNoComments ? "" : formatCommentCountMagazine(wcmNumOfComments)
        const shareclassName = "SiteArticleSocialShareNew1280"
        const shareUrl = typeof window !== "undefined" ? window.location.href : ""
        const readAloud = this.getResolvedReadAloud()
        const isPlaying = isReadAloudOpen && isReadAloudPlaying

        if (verticalSidebar) {
            return (
                <div className={`${shareclassName} article-actions-bar-vertical`}>
                    <div className="vertical-share-container">
                        <ShareMenu
                            title={title || ""}
                            shareUrl={shareUrl}
                            articleId={articleId}
                            btnTitle="שיתוף"
                            showPrint={true}
                            showCopyLink={true}
                            onShare={(platform) => analyticsPushFunc(platform, "Article Vertical Bar", title)}
                        />
                        <button
                            className="vertical-wa-btn"
                            onClick={() => openWhatsAppShare(shareUrl, () => analyticsPushFunc("whatsapp", "Article Vertical Bar", title))}
                        >
                            <span className="vertical-wa-icon" />
                        </button>
                    </div>
                    {showComments && (
                        <button className="vertical-comments-btn" onClick={this.handleCommentsBtnClicked}>
                            <span className={`vertical-comments-icon ${hasNoComments ? "no-comments" : "has-comments"}`}>
                                {commentNumber && <span className="vertical-comment-count">{commentNumber}</span>}
                            </span>
                        </button>
                    )}
                    {readAloud && (
                        <YplusPremiumGated>
                            <button
                                type="button"
                                className={`audio-btn vertical-listen-btn${isPlaying ? " is-playing" : ""}`}
                                onClick={this.handleReadAloudBtnClicked}
                                aria-label={isPlaying ? "השהה האזנה" : "האזנה לכתבה"}
                            >
                                <span className="vertical-listen-text">להאזנה</span>
                                <span className={`vertical-listen-icon${isPlaying ? " vertical-listen-icon--playing" : ""}`} />
                            </button>
                        </YplusPremiumGated>
                    )}
                </div>
            )
        }

        const shouldShowShare = showShare !== false
        const langToUse = lang ? lang.slice(0, 2) : "he"
        const commentsLabel = lang && lang.includes("en") ? "comments" : "תגובות"
        const audioText = readAloud ? formatReadAloudDuration(readAloud.durationSeconds, langToUse) : ""
        const audioIconClass = isPlaying ? "audio-icon audio-icon--playing" : "audio-icon"
        const commentsBtn = showComments && (
            <button className="action-btn comments-btn" onClick={this.handleCommentsBtnClicked}>
                <span className={"action-btn-icon " + (hasNoComments ? "comments-icon--add" : "comments-icon")}>
                    {!hasNoComments && commentNumber && <span className="comment-count">{commentNumber}</span>}
                </span>
                <span className="action-btn-text">{hasNoComments ? "הוספת תגובה" : commentsLabel}</span>
            </button>
        )

        return (
            <div className="siteArticleShareActionBarSummaryWrapper">
                <div className={`${shareclassName} article-actions-bar ${langToUse}${isMagazine ? " article-actions-bar--magazine" : ""}`}>
                    {shouldShowShare && (
                        <ShareMenu
                            title={title || ""}
                            shareUrl={shareUrl}
                            articleId={articleId}
                            btnTitle={isMagazine ? undefined : "שיתוף"}
                            showPrint={true}
                            showCopyLink={true}
                            showTelegram={isMagazine}
                            showAndroidShare={isMagazine}
                            onShare={(platform) => analyticsPushFunc(platform, "Article Top", title)}
                        />
                    )}
                    {shouldRenderSummary && (
                        <ArticleHeader1280AiSummary
                            summaryData={summaryData}
                            articleId={articleId}
                            lang={langToUse}
                            variant="desktop"
                            desktopSummaryMountId={desktopSummaryMountId}
                        />
                    )}
                    {readAloud && (
                        <YplusPremiumGated>
                            <button
                                type="button"
                                className="action-btn audio-btn"
                                onClick={this.handleReadAloudBtnClicked}
                                aria-label={isPlaying ? "השהה האזנה" : "האזנה לכתבה"}
                            >
                                <span className={`action-btn-icon ${audioIconClass}`} />
                                <span className="action-btn-text">{audioText}</span>
                            </button>
                        </YplusPremiumGated>
                    )}
                    {commentsBtn}
                </div>
                {this.renderReadAloudPlayer()}
            </div>
        )
    }
}
